import React, { useState, useEffect } from "react";

import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
  } from "./ui/sheet";
  import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
  import { Mail, Calendar, QrCode, User } from "lucide-react";
import { qrCodesApi } from "../utils/api";
import { QRCode } from "../types";
import { formatDateTime } from "../utils/formatters";
  
  interface QRCodeDetailDrawerProps {
    qrCodeId: string | null;
    isOpen: boolean;
    onClose: () => void;
    codeData: any;
  }
  
  export function QRCodeDetailDrawer({ qrCodeId, isOpen, onClose, codeData }: QRCodeDetailDrawerProps) {
    const [qrCode, setQrCode] = useState<QRCode | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    useEffect(() => {
      const fetchQrCodeDetails = async () => {
        if (!qrCodeId) return;
        setLoading(true);
        setError(null);
        try {
          const response = await qrCodesApi.getQrCodeById(qrCodeId);
          setQrCode(response.data);
        } catch (err) {
          console.error("Error fetching QR code details:", err);
          setError("Failed to load QR code details.");
        } finally {
          setLoading(false);
        }
      };

      if (isOpen && qrCodeId) {
        fetchQrCodeDetails();
      }
    }, [qrCodeId, isOpen]);

    if (!isOpen || !qrCodeId) return null; // Don't render if not open or no QR code selected

    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle>QR Code Details</SheetTitle>
          </SheetHeader>
          
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <p className="text-muted-foreground">Loading QR code details...</p>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center py-8">
              <p className="text-destructive">{error}</p>
            </div>
          ) : qrCode ? (
            <div className="mt-6 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <QrCode className="w-8 h-8 text-primary" />
                    <div>
                      <p className="font-medium">QR Code #{qrCode.code_number}</p>
                      <p className="text-sm text-muted-foreground">Unique ID: {qrCode.qrid}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>Created on {formatDateTime(qrCode.createdAt)}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span>Batch ID: {qrCode.batch_id}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span>Status: {qrCode.status}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span>Active: {qrCode.is_active ? "Yes" : "No"}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Assigned To</CardTitle>
                </CardHeader>
                <CardContent>
                  {qrCode.assigned_to ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span>{qrCode.assigned_to.name} ({qrCode.assigned_to.email})</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">No user assigned</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Created By</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>{codeData?.admin.name}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex justify-center items-center py-8">
              <p className="text-muted-foreground">No QR code details available.</p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    );
  }
